/**
 * Number Input with Plus/Minus Buttons on Sides
 *
 * Number input with increment/decrement buttons on both sides
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import * as LucideIcons from 'lucide-react';
import type { FieldRenderer } from '@/MyCode/core';

/**
 * Number with Plus/Minus Buttons Renderer
 * Features:
 * - Minus button on left, Plus button on right
 * - Manual input in center
 * - Min/max/step constraints
 * - Disabled states when at limits
 */
export const renderNumberWithPlusMinusSides: FieldRenderer = ({
    field,
    value,
    hasError,
    handleChange,
    isRequired
}) => {
    const numericValue = typeof value === 'number' ? value : (value ? parseFloat(value) : 0);
    const step = field.step || 1;
    const minValue = typeof field.minValue === 'number' ? field.minValue : 0;
    const maxValue = typeof field.maxValue === 'number' ? field.maxValue : Infinity;

    const handleIncrement = () => {
        const newValue = Math.min(numericValue + step, maxValue);
        handleChange(field.name, newValue);
    };

    const handleDecrement = () => {
        const newValue = Math.max(numericValue - step, minValue);
        handleChange(field.name, newValue);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (val === '' || val === '-') {
            handleChange(field.name, val);
        } else {
            const numVal = parseFloat(val);
            if (!isNaN(numVal)) {
                handleChange(field.name, numVal);
            }
        }
    };

    const MinusIcon = LucideIcons.Minus;
    const PlusIcon = LucideIcons.Plus;

    return (
        <div className="space-y-2">
            <Label htmlFor={field.name}>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <div className="relative inline-flex h-9 w-full items-center overflow-hidden rounded-md border border-input text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 data-[error=true]:focus-within:border-destructive data-[error=true]:focus-within:ring-destructive/20" data-error={hasError}>
                <Button
                    type="button"
                    onClick={handleDecrement}
                    disabled={numericValue <= minValue}
                    className="-ms-px flex aspect-square h-[inherit] items-center justify-center rounded-s-md border border-input bg-background text-sm text-muted-foreground/80 transition-[color,box-shadow] hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <MinusIcon size={16} aria-hidden="true" />
                </Button>
                <Input
                    id={field.name}
                    type="number"
                    value={value ?? ''}
                    onChange={handleInputChange}
                    min={minValue}
                    max={maxValue}
                    step={step}
                    className="w-full grow bg-background px-3 py-2 text-center tabular-nums border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <Button
                    type="button"
                    onClick={handleIncrement}
                    disabled={numericValue >= maxValue}
                    className="-me-px flex aspect-square h-[inherit] items-center justify-center rounded-e-md border border-input bg-background text-sm text-muted-foreground/80 transition-[color,box-shadow] hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <PlusIcon size={16} aria-hidden="true" />
                </Button>
            </div>
        </div>
    );
};

export default renderNumberWithPlusMinusSides;
